// utils/ownershipTransfer.js
//
// Admin Setup → Ownership Transfer (Ownership design v2.5, parts 4, 5 and 8; wireframe admin-console-wireframe:
// the "Transfer Ownership" page, its transfer and "Transfer everything from a person" popups). HR Admin only; the
// page is not filtered by the HR Admin's own view scope. Pure helpers (wording, columns, filters, the popups' rules),
// so they are tested without rendering. The server makes the final call (who can be the new owner, what moves).

import { JD_STATUSES, getJdStatusDisplay } from 'constants/jobs.constants';

export const OWNERSHIP_TRANSFER_ROUTE = '/admin-setup/ownership-transfer';
export const TAB = { JOBS_LIBRARY: 'jl', PAIC: 'paic' };

// ── Wording (wireframe) ───────────────────────────────────────────────────────
export const PAGE_TITLE = 'Transfer Ownership';
export const PAGE_SUBTITLE = 'Move JDs and People + AI projects to a new owner, for example when someone changes role '
  + 'or leaves';
export const TRANSFER_ALL_BUTTON = 'Transfer everything from a person';
export const TAB_LABELS = { [TAB.JOBS_LIBRARY]: 'Jobs Library', [TAB.PAIC]: 'People + AI' };
export const SEARCH_PLACEHOLDER = 'Search role ID, job title or owner…';
export const COMING_SOON = 'Coming Soon..';
export const NO_MATCH = 'No JDs match.';
export const NO_ONE = '—';
export const UNCLAIMED = 'Unclaimed';
export const INACTIVE = 'Inactive';
export const IN_REVIEW = 'In review';
export const NOT_IN_REVIEW = 'Not in review';
export const REASSIGN_LINK = 'Reassign';
export const TOOLTIP_REASSIGN = 'Hand this review to another HR Manager';
export const CLEAR_ALL = 'Clear all';
export const CLEAR_SELECTION = 'Clear selection';
export const SELECT_FIRST = 'Select JDs first';
export const NOTHING_SELECTED = 'Nothing selected';
export const SELECT_HINT = 'Select JDs to move them to a new owner';
const plural = (n, word) => `${word}${n === 1 ? '' : 's'}`;
export const getSelectedText = (n) => `${n} selected`;
export const getTransferButton = (n) => (n ? `Transfer ${n} ${plural(n, 'JD')}` : 'Transfer JDs');
export const getCountText = (shown, total) => `${shown} of ${total} ${plural(total, 'JD')}`;
export const getApproverText = (name) => `Approver: ${name}`;

// The transfer popups
export const TRANSFER_TITLE = 'Transfer ownership';
export const TRANSFER_ALL_TITLE = TRANSFER_ALL_BUTTON;
// owners: the named owners of the selection (Unclaimed JDs have none)
export const getMoveIntro = (n, owners) => (owners.length ? `Move ${n} ${plural(n, 'JD')} from ${owners.join(', ')}:`
  : `Move ${n} unclaimed ${plural(n, 'JD')}:`);
export const NEW_OWNER_LABEL = 'New owner';
export const NEW_OWNER_HELP = 'HR Managers and HR Executives who have every selected JD in their view scope. The '
  + "approver of a JD in review can't become its owner.";
export const NEW_OWNER_PLACEHOLDER = 'Search and select a new owner';
export const getEligibleHelp = (ok, total, n) => `${ok} of ${total} people can take ${n === 1 ? 'this JD' : 'these JDs'}`
  + ". The others are listed with the reason they can't.";
export const CARRY_LABEL = 'Existing shares and delegations';
export const CARRY = 'carry';
export const REMOVE = 'remove';
export const CARRY_OPTIONS = [
  { value: CARRY, title: 'Carry them over',
    text: 'People the JDs are shared with, and any delegates, keep their access.' },
  { value: REMOVE, title: 'Remove them', text: 'Only the new owner has access. They can share again.' },
];
export const getAffectedLine = (names) => (names.length
  ? `Delegates and shared people: ${names.join(', ')}` : 'None of these JDs is delegated or shared.');
export const getPermanenceBanner = (owners, n) => (owners.length ? `This is permanent. ${owners.join(', ')} will no `
  + `longer own ${n === 1 ? 'it' : 'them'}; to move ${n === 1 ? 'it' : 'them'} back, transfer again.`
  : 'This is permanent. To move it back, transfer again.');
export const CANDIDATES_LOAD_FAILED = "The list of people couldn't be loaded. Close this and try again.";
export const CHOOSE_OWNER = 'Choose a new owner';
// Transfer everything from a person (Jobs Library JDs for now: People + AI is coming soon)
export const CURRENT_OWNER_LABEL = 'Current owner';
export const CURRENT_OWNER_PLACEHOLDER = 'Search and select a person';
export const CURRENT_OWNER_HELP = 'Everything they own in the Jobs Library moves together.';
export const PICK_CURRENT_FIRST = 'Pick the current owner first.';
export const PICK_BOTH = 'Pick both people';
export const getOwnsText = (name, n) => `${name} owns ${n} ${plural(n, 'item')}`;
export const getAllEligibleHelp = (ok, total) => (ok ? `${ok} of ${total} people can take all of these. The others are `
  + "listed with the reason they can't." : 'Nobody can take all of these at once. Close this and transfer the items in '
  + 'smaller groups from the tables, to different owners.');
export const getAllBanner = (name) => `This is permanent. ${name} will no longer own any of these; to move them back, `
  + 'transfer again.';
export const getTransferItemsButton = (n) => (n ? `Transfer ${n} ${plural(n, 'item')}` : 'Transfer items');
export const getTransferredToast = (n, name, carried) => `${n} ${plural(n, 'JD')} transferred to ${name}. Shares and `
  + `delegations ${carried ? 'carried over' : 'removed'}.`;
export const TRANSFER_FAILED_TITLE = 'Ownership not transferred';
export const TRANSFER_PARTIAL_TITLE = 'Some JDs were not transferred';
export const TRANSFER_REMOVED_TITLE = 'Some access was not carried over';

// The reassign popup
export const REASSIGN_TITLE = 'Reassign approver';
export const NEW_APPROVER_LABEL = 'New approver';
export const NEW_APPROVER_PLACEHOLDER = 'Search HR Managers…';
export const getReassignNote = (approverName) => 'The new approver can\'t be the owner, the delegate or anyone the '
  + `draft is shared with. ${approverName || 'The current approver'} stops being the approver; the review keeps its `
  + 'comments and status.';
export const getReassignedToast = (req, name) => `${req} is now with ${name} for approval.`;
export const REASSIGN_FAILED_TITLE = 'Approver not reassigned';

// ── Rows ──────────────────────────────────────────────────────────────────────
// A JD's status pill exactly as the Jobs Library landing page shows it (its label, colour and style), from the family
// status: the same helper the Jobs Library uses, so the two pages never disagree
export const getStatusDisplay = (status) => getJdStatusDisplay({ jd_status: status, family_status: status });
export const getStatusLabel = (status) => getStatusDisplay(status).label;

const REVIEW_STATUSES = new Set([JD_STATUSES.PENDING_APPROVAL, JD_STATUSES.NEEDS_REVISION]);
// Reassign is offered only on rows in review that have an approver
export const isInReview = (row) => REVIEW_STATUSES.has(row?.status) && Boolean(row?.approver?.email);

const name = (person) => person?.name || person?.email || '';
export const ownerName = (row) => (row?.owner?.email ? name(row.owner) : UNCLAIMED);
const delegateNames = (row) => (row?.delegate?.email ? [name(row.delegate)] : []);
const shareNames = (row) => (row?.shares || []).map(name);

// The table's columns, in order (after the checkbox), and what SbTable's own column filter filters each on (several
// values for the people columns)
export const COLUMNS = [
  { key: 'req', title: 'Role ID', filterValue: (r) => r.sb_requisition_number || NO_ONE },
  { key: 'title', title: 'Job Title', filterValue: (r) => r.title || NO_ONE },
  { key: 'owner', title: 'Owner', filterValue: (r) => ownerName(r) },
  { key: 'status', title: 'Status', filterValue: (r) => getStatusLabel(r.status) },
  { key: 'approval', title: 'Approval', filterValue: (r) => (isInReview(r) ? IN_REVIEW : NOT_IN_REVIEW) },
  { key: 'delegation', title: 'Delegation', filterValue: (r) => (delegateNames(r).length ? delegateNames(r) : NO_ONE) },
  { key: 'sharing', title: 'Sharing', filterValue: (r) => (shareNames(r).length ? shareNames(r) : NO_ONE) },
];
const TITLES = Object.fromEntries(COLUMNS.map((c) => [c.key, c.title]));

// The global search: role ID, job title, owner, delegate or shared people
export const matchesSearch = (row, term) => {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return true;
  return [row.sb_requisition_number, row.title, row.job_id, ownerName(row), ...delegateNames(row), ...shareNames(row)]
    .some((v) => String(v || '').toLowerCase().includes(q));
};
// Its suggestions (as RoleReporting / JD Archive search): every role ID, title and owner on the page
export const getSearchOptions = (rows) => [...new Set((rows || []).flatMap((r) => [r.sb_requisition_number, r.title,
  r.owner?.email ? ownerName(r) : null]).filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  .map((value) => ({ value, label: value }));

// The active column filters (SbTable's controlled `filteredValue`, { key: [values] }) as chips: [{ key, label }]
export const getFilterChips = (filters) => Object.entries(filters || {})
  .filter(([key, values]) => TITLES[key] && values?.length)
  .map(([key, values]) => ({ key, label: `${TITLES[key]}: ${values.join(', ')}` }));

// ── The transfer popups ───────────────────────────────────────────────────────
const unique = (people) => [...new Map(people.filter((p) => p?.email).map((p) => [p.email, p])).values()];
// The named owners of these JDs (an Unclaimed JD has none)
export const getOwnerNames = (rows) => [...new Set((rows || []).filter((r) => r.owner?.email).map(ownerName))];
// The delegates and shared people of these JDs, by name (the carry-over choice names them)
export const getAffectedNames = (rows) => unique((rows || []).flatMap((r) => [r.delegate, ...(r.shares || [])]))
  .map(name);

// One builder for the people dropdowns: the people who can be picked first, then those who can't (disabled, with the
// reason); `describe` words what follows each name
const buildPeopleOptions = ({ candidates = [], excluded = [] } = {}, describe) => [
  ...candidates.map((c) => ({ value: c.value, label: describe(c) })),
  ...excluded.map((c) => ({ value: c.value, label: describe(c, c.reason), disabled: true })),
];
// The new-owner dropdown (wireframe ssList): "Name — role · scope", and for someone who can't, the reason after it
export const getCandidateOptions = (data) => buildPeopleOptions(data, (c, reason) => [
  c.label, [c.role, c.scope].filter(Boolean).join(' · '), reason].filter(Boolean).join(' — '));
export const countPeople = (data) => ({ ok: (data?.candidates || []).length,
  total: data?.total ?? (data?.candidates || []).length + (data?.excluded || []).length });
export const personName = (data, email) => [...(data?.candidates || []), ...(data?.excluded || [])]
  .find((p) => p.value === email)?.label || email;

// Transfer everything from a person: the people who own JDs, with their counts, and what each owns
export const getOwnerChoices = (rows) => {
  const byOwner = new Map();
  (rows || []).filter((r) => r.owner?.email).forEach((r) => {
    const entry = byOwner.get(r.owner.email) || { value: r.owner.email, name: name(r.owner), count: 0 };
    entry.count += 1;
    byOwner.set(r.owner.email, entry);
  });
  return [...byOwner.values()].sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({ value: o.value, name: o.name, label: `${o.name} — ${o.count} ${plural(o.count, 'JD')}` }));
};
export const getOwnedRows = (rows, email) => (rows || []).filter((r) => r.owner?.email === email);

// The reassign popup's options: "Name (role)", and for someone who can't, "Name – reason"
export const getOwnerOptions = (data) => buildPeopleOptions(data, (c, reason) => (reason ? `${c.label} – ${reason}`
  : `${c.label}${c.role ? ` (${c.role})` : ''}`));

// The server's per-item results: how many moved, the items that couldn't move (with why), and the people the re-check
// took off (part 8) — not those the HR Admin chose to remove ("not carried over")
export const NOT_CARRIED_OVER = 'not carried over';
export const summarizeTransfer = (response) => {
  const results = response?.results || [];
  return {
    moved: results.filter((r) => r.status === 'moved').length,
    skipped: results.filter((r) => r.status === 'skipped').map((r) => `${r.label || r.job_id || r.id}: ${r.message
      || r.reason}`),
    removed: results.flatMap((r) => (r.removed || []).filter((p) => p.reason !== NOT_CARRIED_OVER)
      .map((p) => `${p.name || p.email} (${r.label}): ${p.reason}`)),
  };
};

// A refused request (an axios error): the server's message, else the fallback
export const getAdminError = (error, fallback) => error?.response?.data?.message || fallback;
